import { unref } from "vue";

/**
 * Keeps one item in a composite widget in the tab order and moves it with the
 * supplied keyboard bindings. Callers own selection and focus details so the
 * composable can serve tabs, radios, lists, and other roving-focus widgets.
 *
 * Callers filter disabled items out of the collection and implement blocked
 * interaction, activation keys, and typeahead separately. An absent current item
 * starts at index zero. Tabindex is pull-based; callers reflect it when state changes.
 *
 * @template T
 * @param {Object} options
 * @param {import("vue").MaybeRef<T[]>} options.items Ordered navigable items.
 * @param {() => T | undefined} options.getActiveItem Returns the current tab stop.
 * @param {(item: T) => void} [options.activate] Activates the destination item.
 * @param {(item: T) => void} options.focus Focuses the destination item.
 * @param {Record<string, "first" | "last" | 1 | -1>} options.keys Keyboard bindings.
 * @returns {{getTabindex: (item: T) => number, handleKeydown: (event: KeyboardEvent, current: T, navigation?: "first" | "last" | 1 | -1) => void}}
 */
export function useRovingTabindex({ items, getActiveItem, activate, focus, keys }) {
  function getTabindex(item) {
    const collection = unref(items);
    const tabbableItem = getActiveItem() ?? collection.at(0);

    return item === tabbableItem ? 0 : -1;
  }

  function handleKeydown(event, current, navigation = keys[event.key]) {
    if (
      navigation === undefined ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.isComposing ||
      event.defaultPrevented
    ) {
      return;
    }

    event.preventDefault();
    const destination = destinationFor(current, navigation);
    if (destination === undefined) {
      return;
    }

    if (destination === current) {
      return;
    }

    activate?.(destination);
    focus(destination);
  }

  function destinationFor(current, navigation) {
    const collection = unref(items);
    if (!collection.length) {
      return undefined;
    }

    if (navigation === "first") {
      return collection.at(0);
    }
    if (navigation === "last") {
      return collection.at(-1);
    }

    const currentIndex = collection.indexOf(current);
    const startIndex = currentIndex === -1 ? 0 : currentIndex;
    const destinationIndex = (startIndex + navigation + collection.length) % collection.length;

    return collection[destinationIndex];
  }

  return { getTabindex, handleKeydown };
}
